/* ═══════════════════════════════════════════════════════════════
   Collab seeding - the ONE way a shared document is filled from the JSON
   row the first time it opens (docs/SPEC_WORKFLOW.md §5). Used by the Spec
   Studio (master drafts) and the Designer (project specs). Exactly one
   writer - the seat the server names - seeds, only after the first sync,
   only into an empty never-seeded document (`shouldSeed`, collabState.ts);
   everyone else waits for the fragment to arrive.
   ═══════════════════════════════════════════════════════════════ */

import type * as Y from 'yjs';
import { getSchema } from '@tiptap/core';
import type { Schema } from '@tiptap/pm/model';
import { prosemirrorJSONToYXmlFragment } from 'y-prosemirror';
import type { ContentBlock } from '../../shared/types';
import { DESIGNER_NODES, blocksToDoc } from '../designer/schema';
import { COLLAB_FIELD } from './collabBinding';

/** Transaction origin of a seed (and of a revert's refill): shared, but not
 *  a user edit - nothing mirrors it. */
export const SEED_ORIGIN = 'sf-seed';

let schemaCache: Schema | null = null;
function specSchema(): Schema {
  if (!schemaCache) schemaCache = getSchema(DESIGNER_NODES);
  return schemaCache;
}

/** Replace the fragment's content with `blocks`. */
export function fillFragment(fragment: Y.XmlFragment, blocks: ContentBlock[]): void {
  if (fragment.length) fragment.delete(0, fragment.length);
  prosemirrorJSONToYXmlFragment(specSchema(), blocksToDoc(blocks), fragment);
}

/** Has anyone filled this document already? (flag, or content that arrived.) */
export function isDocSeeded(ydoc: Y.Doc): boolean {
  return (
    ydoc.getMap<unknown>('meta').get('seeded') === true ||
    ydoc.getXmlFragment(COLLAB_FIELD).length > 0
  );
}

/** The seat the server named to seed, or null. */
export function readSeeder(ydoc: Y.Doc): string | null {
  const s = ydoc.getMap<unknown>('meta').get('seeder');
  return typeof s === 'string' ? s : null;
}

/** Fill the document from `blocks` and flag it seeded - ONE transaction, so
 *  a peer never sees a half-seeded document. `extra` adds to the same
 *  transaction (the masters' layers + edited flag). */
export function seedDoc(ydoc: Y.Doc, blocks: ContentBlock[], extra?: () => void): void {
  ydoc.transact(() => {
    fillFragment(ydoc.getXmlFragment(COLLAB_FIELD), blocks);
    extra?.();
    ydoc.getMap<unknown>('meta').set('seeded', true);
  }, SEED_ORIGIN);
}
